// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Color } from '../../../Graphics';
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { MAMode, RSIMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
export class STOCHASTICRSI extends IndicatorScriptBase {
    constructor() {
        super();
        this.rsiValues = [];
        this.prevV = 0;
        this.prevP = 0;
        this.ProjectName = 'Stochastic RSI';
        this.Comments = 'Stochastic x Relative Strength Index';
        this.SetIndicatorLine('StochRSI', Color.CadetBlue, 1, LineStyle.SimpleChart);
        this.SetIndicatorLine('Smoothing', Color.LightSteelBlue, 1, LineStyle.SimpleChart);
        this.SetLevelLine('Up', 80, Color.Gray, 1, LineStyle.ShapedChart);
        this.SetLevelLine('Down', 20, Color.Gray, 1, LineStyle.ShapedChart);
        this.RsiPeriod = 14;
        super.InputParameter(new InputParameterInteger('RsiPeriod', 'RSI Period', 0, 2, 999));
        this.RsiMode = RSIMode.Simple;
        super.InputParameter(new InputParameterCombobox('RsiMode', 'RSI Mode', 1, [
            { Simple: RSIMode.Simple },
            { Exponential: RSIMode.Exponential }
        ]));
        this.SourcePrice = PriceType.Close;
        super.InputParameter(new InputParameterCombobox('SourcePrice', 'RSI Source', 2, [
            { Close: PriceType.Close },
            { Open: PriceType.Open },
            { High: PriceType.High },
            { Low: PriceType.Low },
            { Typical: PriceType.Typical },
            { Medium: PriceType.Medium },
            { Weighted: PriceType.Weighted }
        ]));
        this.StochasticPeriod = 15;
        super.InputParameter(new InputParameterInteger('StochasticPeriod', '%K Lookback', 3, 2, 999));
        this.K = 3;
        super.InputParameter(new InputParameterInteger('K', '%K Smooth', 4, 1, 999));
        this.D = 3;
        super.InputParameter(new InputParameterInteger('D', '%D Smooth', 5, 1, 999));
        this.MAType = MAMode.SMA;
        super.InputParameter(new InputParameterCombobox('MAType', 'Smoothing Type', 6, [
            { Simple: MAMode.SMA },
            { Exponential: MAMode.EMA },
            { Modified: MAMode.SMMA },
            { LinearWeighted: MAMode.LWMA }
        ]));
        this.SeparateWindow = true;
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
        this.UpdateIndexDrawBegin();
    }
    GetIndicatorShortName() {
        return 'StochRSI(' + this.RsiPeriod + ';' + this.StochasticPeriod + ';' + PriceType[this.SourcePrice] + ')';
    }
    getCustomName() {
        return 'StochRSI (' + this.ProjectName + ')';
    }
    UpdateIndexDrawBegin() {
        this.SetIndexDrawBegin(0, this.RsiPeriod + this.StochasticPeriod - 1);
        this.SetIndexDrawBegin(1, this.RsiPeriod + this.StochasticPeriod + this.K + this.D - 3);
    }
    createInternalIndicators() {
        this.maK = this.Indicators.iMAOnArray(this.CurrentData, this.GetStochRSI.bind(this), this.K, this.MAType);
        this.maD = this.Indicators.iMAOnArray(this.CurrentData, this.getPrevValue.bind(this), this.D, this.MAType);
        return [this.maK, this.maD];
    }
    getPrevValue(index) {
        const count = this.CurrentData.Count;
        if (this.RsiPeriod + this.StochasticPeriod + this.K + this.D - 2 > count) {
            return 0;
        }
        return index === 0 ? this.maK.GetValue(0) : this.GetValue(0, index);
    }
    OnQuote() {
        super.OnQuote();
        const cnt = this.CurrentData.Count;
        if (cnt < this.RsiPeriod + this.StochasticPeriod) {
            this.rsiValues.unshift(0);
            return;
        }
        const rsiNew = this.RsiMode === RSIMode.Simple ? this.CalcSimple() : this.CalcExponential();
        this.rsiValues.unshift(rsiNew);
        this.SetValue(0, 0, this.maK.GetValue(0));
        this.SetValue(1, 0, this.maD.GetValue(0));
    }
    CalcSimple() {
        let upSum = 0;
        let downSum = 0;
        for (let i = 0; i < this.RsiPeriod; i++) {
            const diff = this.CurrentData.getPriceByType(this.SourcePrice, i) -
                this.CurrentData.getPriceByType(this.SourcePrice, i + 1);
            if (diff > 0)
                upSum += diff;
            else
                downSum -= diff;
        }
        return downSum !== 0 ? 100 * (1 - 1 / (1 + upSum / downSum)) : 100;
    }
    CalcExponential() {
        const cnt = this.CurrentData.Count;
        const diff = this.CurrentData.getPriceByType(this.SourcePrice, 0) - this.CurrentData.getPriceByType(this.SourcePrice, 1);
        let avgUp = this.prevV;
        let avgDown = this.prevP;
        if (cnt === this.RsiPeriod + 1) {
            let sumUp = 0;
            let sumDown = 0;
            for (let i = 0; i < this.RsiPeriod; i++) {
                const d = this.CurrentData.getPriceByType(this.SourcePrice, i) -
                    this.CurrentData.getPriceByType(this.SourcePrice, i + 1);
                if (d > 0)
                    sumUp += d;
                else
                    sumDown -= d;
            }
            avgUp = sumUp / this.RsiPeriod;
            avgDown = sumDown / this.RsiPeriod;
        }
        else {
            if (diff > 0) {
                avgUp = (this.prevV * (this.RsiPeriod - 1) + diff) / this.RsiPeriod;
                avgDown = (this.prevP * (this.RsiPeriod - 1)) / this.RsiPeriod;
            }
            else {
                avgUp = (this.prevV * (this.RsiPeriod - 1)) / this.RsiPeriod;
                avgDown = (this.prevP * (this.RsiPeriod - 1) - diff) / this.RsiPeriod;
            }
        }
        this.prevV = avgUp;
        this.prevP = avgDown;
        return avgDown !== 0 ? 100 - 100 / (1 + avgUp / avgDown) : 0;
    }
    GetStochRSI(index) {
        if (index + this.StochasticPeriod > this.rsiValues.length) {
            return 0;
        }
        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        for (let j = 0; j < this.StochasticPeriod; j++) {
            const v = this.rsiValues[index + j];
            if (v < min)
                min = v;
            if (v > max)
                max = v;
        }
        const range = max - min;
        return range === 0 ? 0 : (100 * (this.rsiValues[index] - min)) / range;
    }
}
